import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, History, ArrowUpRight, ArrowDownLeft, ArrowLeftRight, SlidersHorizontal } from 'lucide-react';
import inventoryService from '../services/inventoryService';

const StockKardexModal = ({ productId, productName, isOpen, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const { data, isPending: isLoadingMovements } = useQuery({
    queryKey: ['inventory-stock-movements', productId],
    queryFn: () => inventoryService.getStockMovements({ produto_id: productId, limit: 100 }),
    enabled: isOpen && !!productId
  });

  if (!isOpen) return null;

  const movimentos = data?.data?.movimentos || data?.data || [];

  const getTipoBadge = (tipo) => {
    switch (tipo) {
      case 'IN':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800">
            <ArrowDownLeft className="w-3 h-3 text-emerald-600" />
            Entrada (IN)
          </span>
        );
      case 'OUT':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-800">
            <ArrowUpRight className="w-3 h-3 text-red-600" />
            Saída (OUT)
          </span>
        );
      case 'TRANSFER':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800">
            <ArrowLeftRight className="w-3 h-3 text-blue-600" />
            Transferência
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800">
            <SlidersHorizontal className="w-3 h-3 text-amber-600" />
            Ajuste
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-3 sm:p-4 overflow-hidden">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50/70 via-white to-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Extrato Kardex de Movimentações
              </h2>
              <p className="text-xs text-gray-500">
                Histórico contábil e auditoria física de "{productName}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg transition-colors"
            title="Fechar (Esc)"
            aria-label="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLoadingMovements ? (
            <div className="py-16 text-center text-gray-400 text-sm">Carregando histórico...</div>
          ) : movimentos.length === 0 ? (
            <div className="py-16 text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <History className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-700">Nenhuma movimentação registrada</p>
              <p className="text-xs text-gray-400 mt-1">Todas as entradas, saídas e transferências deste item serão listadas aqui.</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-xl">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Data / Hora</th>
                    <th className="px-3 py-2.5 text-left">Tipo</th>
                    <th className="px-3 py-2.5 text-left">Variante</th>
                    <th className="px-3 py-2.5 text-center">Quantidade</th>
                    <th className="px-3 py-2.5 text-left">Armazém / Destino</th>
                    <th className="px-3 py-2.5 text-left">Usuário</th>
                    <th className="px-3 py-2.5 text-left">Observações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {movimentos.map((m) => (
                    <tr key={m.id} className="hover:bg-blue-50/20 transition-colors">
                      <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">
                        {m.data_mov || m.created_at ? new Date(m.data_mov || m.created_at).toLocaleString('pt-BR') : '-'}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {getTipoBadge(m.tipo)}
                      </td>
                      <td className="px-3 py-2.5 font-medium text-gray-900">
                        {m.sku_variacao || m.cor || `Variante #${m.variante_id || '-'}`}
                      </td>
                      <td className="px-3 py-2.5 text-center font-mono font-bold text-gray-900">
                        {m.tipo === 'OUT' ? `- ${parseFloat(m.quantidade).toLocaleString('pt-BR')}` : `+ ${parseFloat(m.quantidade).toLocaleString('pt-BR')}`}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700">
                        {m.armazem_origem_nome || m.armazem_nome || 'Armazém'}
                        {m.armazem_destino_nome ? ` ➔ ${m.armazem_destino_nome}` : ''}
                      </td>
                      <td className="px-3 py-2.5 text-gray-600">
                        {m.usuario_nome || 'Sistema'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 max-w-xs truncate" title={m.observacoes}>
                        {m.observacoes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Total de registros: <strong>{movimentos.length}</strong>
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-semibold rounded-lg transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockKardexModal;
